module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h3', {
    staticClass: "ct"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('b', [_vm._v("2")]), _vm._v(" The Learned Self: Artifice in Brahms’s Late Intermezzi")]), _vm._v(" "), _c('p', {
    staticClass: "chapauthor"
  }, [_c('a', {
    attrs: {
      "id": "chap02author"
    }
  }), _c('em', [_vm._v("Steven Rings")])]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("An Example by Way of Introduction")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We begin by exploring aspects of Brahms’s Intermezzo in A Major, op. 118, no. 2, focusing on the passages shown in Examples 2.1a and b. Example 2.1a presents the opening theme, while 2.1b shows a later transformation in mm. 34–36. Examples 2.1c and d make the nature of the transformation clear: the melody of mm. 34–36 inverts that of mm. 0–2. The down-stemmed, parenthesized eighths in Example 2.1e show a canonic imitation of the inverted theme one octave lower, at a temporal interval of three eighth notes, which creates a subtle hocket effect.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "example2.1"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f019-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.1. The opening theme of the Intermezzo in A Major, op. 118, no. 2, and its later transformation in mm. 34–36.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }), _vm._v("This already suggests that Example 2.1b is a moment of quiet, composerly achievement. But there is more still to notice. First, observe that the inverted theme in 2.1d occupies the same registral band as does the recto statement in 2.1c: they have the same lower and upper pitch boundaries, B and A. Indeed, the operation that maps the recto theme onto its inversion is the diatonic pitch inversion that maps these outer pitches onto one another."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn1"
    }
  }, [_vm._v("1")])]), _vm._v(" B and A have a highly sensitive character in the opening theme itself. In Example 2.1a, B proves unable to descend to A, instead consistently leaping up across the bar line; these leaps are central to the gestural physiognomy of the piece."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" They are mirrored by leaps down in the bass. These mirroring outer-voice leaps lend a subtle dialectical energy to the intermezzo’s pervasive "), _c('em', [_vm._v("auftaktig")]), _vm._v(" gestures: while the bass agrees with the kinetic profile of such an "), _c('em', [_vm._v("Auftakt")]), _vm._v(" (up–DOWN), the melody gently contradicts it (down–UP), giving extra poignancy to the local melodic apexes of D (m. 1) and A (m. 2). The B–A leap across the bar line into m. 2 is especially striking, as it is at once an intervallic expansion of the previous B–D leap as well as a paradoxical “resolution”: B now proceeds to A, but it is the “wrong” A, one octave too high."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" As a result, the high A has a curiously unstable quality due to its register, despite its intervallic consonance with the bass."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" The arpeggiation in the right hand on the downbeat of m. 2 further marks the moment, giving the leap a vocal intensity, as Charles Rosen has noted, by slightly delaying the arrival of the high A in the manner of a singer ascending to that pitch (and belying the ease with which the leap might otherwise be played on the piano)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" All of these factors combine to make the B–A leap an expressive locus for the piece, a gesture to which the player will return again and again, its harmonic lighting often subtly varied.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The space opened up by the B–A leap serves as the frame for the inversion of the subject in mm. 34–36; upward leaps become downward leaps, A now leaps down to B. The inversion has a striking effect on the motive—the change in direction across the bar lines now has a feeling of "), _c('em', [_vm._v("release,")]), _vm._v(" of easing back after the reach up to A, as opposed to the mild infusion of tension projected by the upward leaps of the recto statement. The inverted motives now land on downbeats that have a feeling of relative stability. This change in kinetic profile results not only from the change in intervallic direction but also from an inversion of the motives’ patterns of consonance and dissonance. As indicated above the top staff of Example 2.2a, the three notes of the initial motive project the sequence consonant-dissonant-dissonant. These values invert in mm. 34–36 to dissonant-consonant-consonant, as shown in 2.2b.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The hearing of the melodic D on the downbeat of m. 1 as a dissonant fourth over the bass note A agrees with the harmonic reading below the staff of Example 2.2a, which treats the "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common64.jpg"
    }
  }), _vm._v(" harmony as an embellishing sonority to the opening "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common53.jpg"
    }
  }), _vm._v(". "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }), _vm._v("Comparison with the harmonic reading in Example 2.2b reveals further manifestations of inversional logic. Note first the figured-bass signatures: the stable "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common53.jpg"
    }
  }), _vm._v(" sonority sounds on the upbeat in the recto statement but on the downbeat in the inverted statement, realigning the motive’s harmonic-metric center of gravity from weak upbeat to strong downbeat. As shown in 2.2c and d, the "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common64.jpg"
    }
  }), _vm._v(" and "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common63.jpg"
    }
  }), _vm._v(" sonorities that embellish these "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common53.jpg"
    }
  }), _vm._v(" chords exhibit an inversional logic of their own: the "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common64.jpg"
    }
  }), _vm._v(" neighbors its "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common53.jpg"
    }
  }), _vm._v(" above and after, while the "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common63.jpg"
    }
  }), _vm._v(" neighbors "), _c('em', [_vm._v("its")]), _vm._v(" "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common53.jpg"
    }
  }), _vm._v(" below and before. All of this inversional symmetry results in a reversal of harmonic priority, as shown in the roman numerals beneath 2.2a and b: the tonic harmony decorated by a putative IV suffix in the recto statement becomes a subdominant harmony decorated by a putative I prefix in the inversion.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "example2.2"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f021-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.2. Harmony in mm. 0–1 and 34–35 of the Intermezzo in A Major, op. 118, no. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The neighboring role of the putative I in the inversional statement is made clear in Example 2.3a. The initial three-note motive appears in the bass at the upbeat to m. 31, with C"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v(" serving as a neighbor to D in the local harmonic context."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" The textural inversion—with the motto now sounding in the bass, not in the soprano—is thus paired with an inversion in harmonic priority: the putative I harmonies on the upbeats now neighbor the D-based harmonies on the downbeats. The energy of the motive carries through to the inverted statement in the soprano of mm. 34–35; the "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common63.jpg"
    }
  }), _vm._v(" chord over C maintains its neighboring role throughout. Example 2.3b isolates further details in the passage. In addition to the statements of the three-note motive—first in the bass, then inverted in the soprano—the example summarizes the activity of the right hand in mm. 30–34. The upper voice in these bars traces two "), _c('em', [_vm._v("Septzüge")]), _vm._v(" from A down to B. This linear span is, of course, a transposition—one octave down—of the crucial A/B minor seventh of the opening. The melodically sensitive pitch classes, A and B, are fused harmonically in the ii"), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common65.jpg"
    }
  }), _vm._v(" chord that governs mm. 30–34. They are not only the end points of the two "), _c('em', [_vm._v("Septzüge,")]), _vm._v(" they are also played as a simultaneity by the pianist’s right-hand thumb in mm. 31 and 33 (see the down-stemmed half notes in 2.3b). Remarkably, the thumb’s A/B dyad serves as a "), _c('em', [_vm._v("literal axis")]), _vm._v(" of pitch-space inversion, as the three-note motto travels from the bass to its inverted form in the soprano. This axial function is shown in Example 2.3c: the parenthesized A/B dyad is the exact center of pitch-space inversion between minor bass motto and inverted soprano motto. Unlike the inversion explored in Examples 2.1c and d, the inversion here is chromatic, or real: the inflection of the bass motive (and the surrounding harmony) to minor in mm. 33–34 alters the motto’s intervallic profile to m2↓–m3↑, which inverts exactly to m2↑–m3↓ in the soprano."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn7"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn7"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.3"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f022-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.3. Harmony and motive in mm. 30–35 of the Intermezzo in A Major, op. 118, no. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _vm._v("The inversion of the theme in mm. 34–36 is thus a moment of quintessentially dense Brahmsian thought: inversional logic abounds, embracing melody, harmony, mode, register, texture, consonance and dissonance, and metric weight. Even more remarkable than all these signs of compositional craft, however, is the way the music here "), _c('em', [_vm._v("sounds:")]), _vm._v(" Brahms makes the moment of inversion almost unutterably tender. It is not only a formal nodal point for various technical processes in the music, it is also an expressive nodal point—a moment of "), _c('em', [_vm._v("innig")]), _vm._v(" intensity that seems to distill and intensify the particular "), _c('em', [_vm._v("Stimmung")]), _vm._v(" of this intermezzo. The moment sounds so extraordinary in part because of the various details we have explored thus far—inversions in harmonic and melodic kinetics and so forth—but this is not the entire story. Brahms "), _c('em', [_vm._v("stages")]), _vm._v(" the passage in a way that is independent of these local technical details. The event is spotlighted in advance; we know to expect something special as it approaches. A quick review of the rhetorical preparation will make this clear.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("After two statements of the opening period, Brahms begins the contrasting B section of the AABA’ song form with the pickup to m. 17, destabilizing both harmony and meter in the process. In the measures that follow, metric and harmonic dissonance work in a characteristically Brahmsian tandem; it is not until the firm arrival on the dominant on the downbeat of m. 25 that the two resolve simultaneously and we regain our harmonic and metric bearings (this after a wonderfully vertiginous mm. 23 and 24). The buildup over the dominant pedal that ensues seems to suggest the imminent return of the A-major tonic and (one presumes) the opening theme. The theme does arrive, but the tonic does not: a harmonic deflection in mm. 28–29 diverts us from A to D, the subdominant, which arrives in m. 30 with the loudest dynamic in the piece thus far ("), _c('em', [_vm._v("forte")]), _vm._v("). That loud dynamic coincides with the high A of the opening theme, now celebratory rather than diffident. The subdominant pull in the theme, evident since the first downbeat, reaches its apotheosis here, forcing a figure-ground reversal that momentarily displaces the A tonic entirely: D becomes the music’s momentary center of gravity, pulling all other events into its orbit. The music shown in Example 2.3a follows the subdominant breakthrough. The ruminative iterations of the motive in the bass, the shift to minor in m. 33, the "), _c('em', [_vm._v("decrescendo")]), _vm._v(" and "), _c('em', [_vm._v("calando")]), _vm._v(" marking in mm. 33–34—all engender expectation as the subdominant climax ebbs. The music stalls out on a ii"), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common65.jpg"
    }
  }), _vm._v(" chord, causing us to wonder how we can possibly emerge from this harmonic cul-de-sac; in the minor-inflected hush of the first two beats of m. 34, there is a gathering together of attention. Then, on beat 3, minor gives way to major, recto bass theme gives way to inverted treble theme, "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }), _vm._v("dark trombone voicings give way to the shimmering woodwind clarity of the upper register—and the myriad technical marvels outlined above unfold. As so often in Brahms’s late intermezzi, the moment of greatest poetry in the movement is also the moment of greatest artifice.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("Late Brahms, Artifice, and the Intermezzi")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In a valuable recent study, Margaret Notley has explored the role of artifice in Brahms’s late style, arguing that the composer “overtly cultivat[ed] musical artifice in his later music.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn8"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" Christian Martin Schmidt similarly describes Brahms’s late style as “characterized by an uncompromising prominence of artifice, by the far-reaching penetration of the musical details.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn9"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn9"
    }
  }, [_vm._v("9")])]), _vm._v(" While the word "), _c('em', [_vm._v("artifice")]), _vm._v(" carries a potentially negative connotation—suggesting artificiality, undue cleverness, and perhaps even an opposition to “nature”—Schmidt and Notley do not seem to have such connotations in mind. Notley, for one, situates the term within a network of (positive) Viennese Liberal values. She persuasively ties Brahms’s emphasis on compositional craft to the ideals of the Viennese "), _c('em', [_vm._v("Bürgertum,")]), _vm._v(" then under threat from the ascendant Christian Socialist right. She observes that Brahms’s late works embody the Liberal values of intellectual refinement and subtlety, technical achievement, consummate craft, unassailable compositional logic, and hard work (à la Max Weber’s Protestant ethic). All of these characteristics were opposed to what the left viewed as the dangerous sensationalism, superficiality, and illogic of Bruckner, the musical hero of the Viennese right. Additionally, Notley argues, Brahms’s late works can be heard, in various ways, to enact aspects of an inward, reflective, even ironic consciousness in which rationality nevertheless prevails—a sort of sonic image of the Liberal Viennese self."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn10"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn10"
    }
  }, [_vm._v("10")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Brahms’s late intermezzi represent both a vivid manifestation of these ideas and something of a special case."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn11"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn11"
    }
  }, [_vm._v("11")])]), _vm._v(" Due to their status as piano miniatures—technically accessible character pieces to be played by an individual—these works engage issues of inwardness, ironic awareness, rational control, and subjectivity in ways that are not merely abstract but instead potentially enacted at the keyboard in the moment of performance. They do not merely signify as sounds taken in by a listener, they also signify as sets of actions—bodily and expressive—to be performed by a pianist. Such meaning emerges in and through first-person action. As we will see, this makes the intermezzi a particularly fruitful site for an exploration of the ways in which Brahms’s music—with its conspicuous mix of technical accomplishment and expressive immediacy—can be meaningful for one who plays it.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is clear that Brahms and Clara Schumann themselves found the act of playing the late piano works deeply meaningful. On receiving the first batch of "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _vm._v("op. 116 pieces in November 1892, Clara describes them in her diary as “full of poetry, passion, sentiment, emotion, and having the most wonderful effects of tone. . . . In these pieces I at last feel musical life stir once more in my soul, once more I play with real devotion.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn12"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" Clara and Brahms’s epistolary exchange about the Intermezzo in B Minor, op. 119, no. 1, is more revealing still. On sending the piece to her in May 1893, Brahms writes:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("It teems with discords. These may be alright and quite explicable, but you may not perhaps like them, in which case I might wish that they were less right but more pleasing and more to your taste. It is exceptionally melancholy, and to say “to be played very slowly” is not sufficient. Every bar and every note must be played as if "), _c('em', [_vm._v("ritardando")]), _vm._v(" were indicated, and one wished to draw the melancholy out of each one of them, and voluptuous joy and discomfort out of the discords."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn13"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn13"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Clara, 8 June:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("You must have known how enthusiastic I should be when you were copying out that bittersweet piece which, for all its discords, is so wonderful. Nay, one actually revels in the discords, and, while playing them, wonders how the composer ever brought them to birth."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn14"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Brahms’s reply later that month is at once wry and telling:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("I must write a line to tell you how glad I am that my little piece has pleased you. I really had not expected that it would, and now shall be able to enjoy it in peace and calm at my own piano as if I had a license to do so from the head of the police."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn15"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn15"
    }
  }, [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The exchange is fascinating on many levels, especially as regards its intertwining themes of license, rule, and private pleasure. It is hard to know just how to read Brahms’s final statement, with its characteristic mix of apparent sincerity and distancing humor, but the sense of almost illicit private enjoyment that he describes is highly suggestive of traditional narratives about nineteenth-century "), _c('em', [_vm._v("Charakterstücke,")]), _vm._v(" those quintessential vehicles of Romantic interiority, of which Brahms’s opp. 76 and 116–19 are the most notable late-century examples. Only here it is not just any bourgeois subject who plays the music alone, “in peace and calm,” but the composer himself, presumably drawing out its melancholy dissonances."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn16"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn16"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Remarkably, Eduard Hanslick employs similar imagery in his 1896 review of opp. 118 and 119, conveying the music’s effect (and affect) via a highly vivid description of Brahms at the keyboard:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("One can describe these two volumes as “Monologues at the piano”: Monologues that Brahms recites by and for himself during lonely evening hours, defiantly "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }), _vm._v("pessimistic ["), _c('em', [_vm._v("in trotzig-pessimistischer Auflehnung")]), _vm._v("], in brooding contemplation, in Romantic reminiscence, from time to time also in dreamy wistfulness."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn17"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn17"
    }
  }, [_vm._v("17")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Once again, the composer, performing for himself, adopts the role of the solitary subject so familiar from descriptions of the Romantic "), _c('em', [_vm._v("Charakterstück,")]), _vm._v(" communing with himself through the medium of the piano piece. Hanslick’s suggestive addition is his description of the pieces as monologues. He seems to mean the word in the most literal sense: monologues as "), _c('em', [_vm._v("things said to oneself.")]), _vm._v(" Brahms does not deliver these monologues to an audience from a stage, in the manner of a soliloquy; instead, he recites them to himself, over long evenings, as he works through the surfeit of emotions and affective states that Hanslick senses in the music."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn18"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn18"
    }
  }, [_vm._v("18")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Clara, Brahms, and Hanslick all describe Brahms’s late pieces as affording an intensely involving personal experience for the solitary performer."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn19"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn19"
    }
  }, [_vm._v("19")])]), _vm._v(" But what of the music’s pervasive artifice? How does it relate to these images of deeply personal engagement with the music, of emotionally charged performance for oneself as a sort of reflexive monologue? Baldly put, is the music affecting "), _c('em', [_vm._v("because")]), _vm._v(" of its artifice or "), _c('em', [_vm._v("in spite")]), _vm._v(" of it? Or is the relationship more complex than this? To begin, we should observe that the late piano music rarely wears its learnedness on its sleeve: it is worked deep into the fabric of the compositions. Carl Dahlhaus, in a passage that manages at once to invoke Castiglione and Nietzsche, attributes this to the aesthetic principle of "), _c('em', [_vm._v("nascondere l’art:")])]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("Aristocratic taste objected to an obtrusive use of strict counterpoint, regarding it as music that, to borrow Nietzsche’s term, “sweats.” The goal instead was an attitude of light-handed geniality, of "), _c('em', [_vm._v("nobile sprezzatura;")]), _vm._v(" the exertions of musical artifice, if unavoidable, were at least not to be flaunted."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn20"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn20"
    }
  }, [_vm._v("20")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Those in Brahms’s circle occasionally expressed sentiments along these lines. Clara, for example, in a letter to Joachim in 1862, writes of the music that would become the opening allegro of the First Symphony:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("The movement is full of wonderful beauties, with a mastery in the treatment of the motifs which is indeed becoming more and more characteristic of him. Everything is so interestingly interwoven, yet as spirited as a first outburst; one enjoys [it] completely to the full, "), _c('em', [_vm._v("without being reminded of the craft.")]), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn21"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn21"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Clara thus holds it as a virtue that Brahms’s symphony does not make a point of its artifice, or at least does not let that artifice distract the listener from enjoying its many other “wonderful beauties.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There is thus some truth to Dahlhaus’s statement, but something about it still falls wide of the mark. For one, attribution of such an aesthetic stance to a proto-Nietzschean "), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }), _vm._v("aversion to “music that sweats” rings false. After all, Nietzsche himself very likely felt that Brahms’s music "), _c('em', [_vm._v("does")]), _vm._v(" sweat, every bit as much as Wagner’s and in just the way that Bizet’s, for instance, does not."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn22"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" Further, as Notley has noted, there are indeed chamber works by Brahms in which artifice takes on a prominence that shades over into mannerism; not all his artifice is hidden, as Dahlhaus suggests—in many passages compositional exertion is on full display (think of the String Quartet op. 51, no. 1)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn23"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn23"
    }
  }, [_vm._v("23")])]), _vm._v(" Nor does it seem right, when confronting works in which artifice is less overt, to attribute its concealment to a sort of aristocratic squeamishness. This ill fits what we know of Brahms’s character and is especially inapt when it comes to music as private and inward as the late piano works—here, concern for aristocratic decorum seems misplaced.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("I therefore propose that there are other ways to understand the “submerged artifice” in many of Brahms’s late intermezzi. Here it will be useful to return to the A-major intermezzo. The discussion above explored ways in which various formal and expressive processes converge on the moment of thematic inversion in mm. 34–36. As I stated there, the poetic effect of that passage cannot be attributed solely, or even primarily, to the technical processes. It is, rather, a product of the piece’s rhetoric: the ways in which the moment is carefully prepared and staged. Brahms focuses the player’s attention on a nodal point in the music in which a set of highly learned processes comes to a head; he does so, however, "), _c('em', [_vm._v("not")]), _vm._v(" by drawing attention to those processes but by making this a moment of potent expressive concentration. As a result, it is not difficult to imagine a player who knows the piece well, and perhaps even cherishes this moment in it above all others, but is not consciously aware of its thematic inversion. Perhaps the expressive impact of the passage occupies the pianist’s attention to such a degree that awareness of all formal details recedes from consciousness."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn24"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We might thus detect two parallel aesthetic tracks at work in the intermezzo, one concerned with the beauties of sound and expression, the other concerned with the beauties of craft. The two tracks cannot be entirely separated, of course—the sonic beauty of the piece depends to a large degree on its submerged craft, and there is a remarkably seamless fit between technical and expressive contour. As Joachim said in 1877 about the first symphony: “One does not quite know where the enormous virtuosity of workmanship stops and the music begins,” words that apply a fortiori to the A-major intermezzo."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn25"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn25"
    }
  }, [_vm._v("25")])]), _vm._v(" But artifice and affect nevertheless retain a degree of autonomy in the work, as I have proposed above; the work’s "), _c('em', [_vm._v("innig")]), _vm._v(" crux did not "), _c('em', [_vm._v("need")]), _vm._v(" to be a site of such learned procedure. Further, it can hardly be denied that there is a purely formal beauty at work in the intermezzo: the relationships explored in Examples 2.1–3 can themselves be objects of aesthetic contemplation. They have a certain crystalline perfection that is independent of the sound of the music. But it is precisely the sound that draws "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }), _vm._v("the player deeply into the intermezzo. And the fact that both the affective and learned processes in the piece reach fulfillment in the same moment is deeply telling. The affective stratum almost seems to express a stance toward the technical relationships, to "), _c('em', [_vm._v("value")]), _vm._v(" them via its expressive contours. It is as though the music sonically interprets its own formal values.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The player, in turn, comes to know these affective and technical strata through a complex mixture of somatic, intellectual, and affective processes. The relative proportions of these processes will vary from individual to individual due to differences in temperament, training, enculturation, and so forth. But for all players, the process of learning to re-create the piece at the instrument will involve, among other things, an act of assimilation—or perhaps even incorporation (in the most literal sense)—in which the boundary between player and piece becomes difficult to fix. The music, when well learned, can seem to become something "), _c('em', [_vm._v("of")]), _vm._v(" the player. This idea is reflected in certain familiar locutions, as when we say that a player has made a given piece his or her “own” (one thinks, perhaps, of Glenn Gould and the "), _c('em', [_vm._v("Goldberg Variations")]), _vm._v("). But the very fact that familiar music can continue to move a performer suggests that it in some way maintains its autonomy. That autonomy is physically reified by the score sitting on the piano in front of the player; it is also manifested in the physical sound that surrounds the pianist, sound that the pianist at once produces and reabsorbs in the act of listening. Player and music thus constitute a sort of a dialectical circuit in which “the music” is at once something of the playing subject and simultaneously an object for that subject.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This idea is especially suggestive when we think of Brahms at his keyboard alone, responding to music that he himself composed—music that is “of him” in a very strong sense but that retains the power to move him. Particularly suggestive is the impression Brahms gives of repeatedly playing the B-minor intermezzo: having received Clara’s blessing regarding its opening dissonances, he can now “enjoy it in peace and calm at [his] own piano”; the language strongly evokes meditative, repeated playings. This suggests that, even for its composer, the music affords a sort of inexhaustible interaction, existing in a state of continual discovery."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn26"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn26"
    }
  }, [_vm._v("26")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Of course, this “continual discovery” differs in important ways for players who are not the composer. For those players, the density of the music’s formal argument abets the process of repeated and varied engagement: there is a persistent gap between what one knows about the work and what one "), _c('em', [_vm._v("could")]), _vm._v(" know about it. That gap opens a space in which the player’s relationship to the piece can evolve and change."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn27"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn27"
    }
  }, [_vm._v("27")])]), _vm._v(" Surely the most vivid contemporary accounts of this kind of engagement with Brahms’s music are to be found in Elisabet von Herzogenberg’s letters to the composer. She describes a process of coming to know Brahms’s "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }), _vm._v("works—of playing them, living with them, and experiencing them at various stages of acquaintance—that is at once revealing of the values prized by the Brahms circle and indicative of the sort of interaction with his music that Brahms might have hoped for from an ideal listener or player. None of Herzogenberg’s letters are more suggestive in this regard than those regarding the Fourth Symphony. Though these letters concern a symphony and not a short character piece, they are highly apposite to our present discussion, as they reveal Herzogenberg’s process of coming to know a piece at the piano."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn28"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" Writing on 8 September 1885, she describes the process of first getting acquainted with the work:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("[The symphony] is becoming ever more alive ["), _c('em', [_vm._v("Lebendiger")]), _vm._v("] and clear to me, and it consistently reveals new mysteries. . . . Your piece affects me curiously: the more penetration I bring to bear on it, the more impenetrable it becomes; the more stars define themselves in the twilight glow, which at first served to hide them; the more distinct sources of joy do I have, some expected, some unexpected; and the more plainly can I trace the great central driving power which gives unity to the complex work. One never wearies of straining eyes and ears to grasp all the clever turns, all the strange illuminating effects of rhythm, harmony, and color, or of admiring your fine chisel for its firm and delicate strokes. Indeed the possibilities are so inexhaustible that one experiences the joys of a discoverer or a naturalist at every new evidence of your creative ingenuity."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn29"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn29"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Herzogenberg’s language is remarkable: the symphony is not only becoming clearer to her, it is also becoming “more alive” ("), _c('em', [_vm._v("Lebendiger")]), _vm._v("). She likens musical details to stars that were only dimly perceived at first but are now seen more clearly; the analogy to the observation of the physical world is made explicit when she compares the process of discovery to that of a naturalist ("), _c('em', [_vm._v("Naturforscher")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As her comments about the piece’s persistent impenetrability suggest, however, Herzogenberg was at this point still critical of the work’s abundance of artifice. Tellingly, she worries that this technical complexity will work "), _c('em', [_vm._v("against")]), _vm._v(" the symphony’s expressive directness:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("Yet it seems to me that if its actual appeal proves simple and direct, the effect is only gained "), _c('em', [_vm._v("at the cost of")]), _vm._v(" all that tangled overgrowth of ingeniously interwoven detail, which must be overlooked if one would taste and enjoy the fruit itself."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn30"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn30"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Writing some three weeks later, however, Herzogenberg’s relationship to the piece has changed (though one senses that her reservations have not disappeared completely). Enclosing the 8 September note within a longer letter of 31 September, she says:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote1"
  }, [_vm._v("How glad I am now that I did not air my half-formed impressions, for I know so much more about it today—the dear E-minor movement—and have played it so "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }), _vm._v("often, inwardly and at the piano, devoting to it practically every free minute . . . that I am now wholly intimate with it, and many of the remarks I made the other day now seem quite inappropriate. If I send the other shred of a letter all the same, it is with the idea that you might be interested in following the workings of a plain person’s mind in chronological order.")]), _vm._v(" "), _c('p', {
    staticClass: "blockquotei"
  }, [_vm._v("I can now trace the hills and valleys so clearly that I have lost the impression of its being a complicated movement; or rather I no longer look on the complication as detrimental to its effect in any way. At worst it seems to me as if a great master had made an almost extravagant display of his skill! I was glad to see how great an effect it "), _c('em', [_vm._v("could")]), _vm._v(" have when I played it to my sister at Hosterwitz. She was quite carried away by the general sound and character of the movement in spite of the inadequate performance, and she is, I may say, a good example of the intelligent but wholly uninitiated listener. She never noticed the points with which we were chiefly concerned—the ingenious combination of the themes, the massing together of separate links, but simply enjoyed what she heard. And it is the same with me, now that I have heartily absorbed it; my piece ["), _c('em', [_vm._v("mein Stück")]), _vm._v("] makes me immensely happy, and I have a furious longing to "), _c('em', [_vm._v("hear")]), _vm._v(" it soon."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn31"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn31"
    }
  }, [_vm._v("31")])])]), _vm._v(" "), _c('p', {
    staticClass: "para2"
  }, [_vm._v("The details worthy of mention in the letter are too numerous to address in all. We should simply note Herzogenberg’s vivid account of her evolving relationship to the piece, which has developed through her playing it “inwardly and at the piano” ("), _c('em', [_vm._v("innerlich und am Klavier")]), _vm._v(") in “practically every free minute” in the three-plus weeks since the previous note. In the process, she has become “wholly intimate” ("), _c('em', [_vm._v("ganz intim")]), _vm._v(") with the work. Most strikingly, she says she has now absorbed the symphony: “now that I have heartily gulped everything down” (nun ich alles gehörig in mich hineingeschluckt habe); one can hardly imagine a more vivid image of a player assimilating a piece and making it her own—the assimilation in Herzogenberg’s language is literally corporeal. She underscores the point by referring to the work now as “my piece” ("), _c('em', [_vm._v("mein Stück")]), _vm._v(")."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn32"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn32"
    }
  }, [_vm._v("32")])]), _vm._v(" And yet the fact that her relationship to the work continues to evolve and change suggests that the symphony maintains a degree of relative autonomy from her—as a beguiling but occasionally perplexing other.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In a passage that resonates with Herzogenberg’s description of her own experience with the symphony, Hanslick discusses the expressive and compositional complexity of the intermezzi. He says that they represent a kind of music that “does not win one over at first glimpse” (die nicht auf den ersten Blick gewinnt) but that instead requires effort to get to know. More cryptically, he states about opp. 118 and 119: “There is much ore ["), _c('em', [_vm._v("Eisengehalt")]), _vm._v("] buried in these pieces, and this ore will long be conserved.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn33"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn33"
    }
  }, [_vm._v("33")])]), _vm._v(" Hanslick seems to suggest that the works’ technical and expressive details can be mined only with difficulty by the player or interpreter; they are thus not easily exhausted."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn34"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn34"
    }
  }, [_vm._v("34")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }), _vm._v("This meaning becomes explicit a few sentences later. After comparing Brahms’s music to that of Spohr, he says: “The new Brahms pieces do not speak immediately to the soul ["), _c('em', [_vm._v("Gemüt")]), _vm._v("], nor flatteringly to the ear; for that reason, they do not need to fear such early withering ["), _c('em', [_vm._v("Abwelken")]), _vm._v("].” The contrast between the inorganic "), _c('em', [_vm._v("Eisengehalt")]), _vm._v(" and organic withering is striking; Brahms’s works are at once valorized for their durability, yet that durability is purchased at the cost of a certain hardness, even coldness. One might disagree with this assessment—uninitiated listeners (listeners like Elisabet von Herzogenberg’s sister) often do react immediately and powerfully to the intermezzi, suggesting that they "), _c('em', [_vm._v("can")]), _vm._v(" speak directly to the "), _c('em', [_vm._v("Gemüt.")]), _vm._v(" But it is clear that, for Hanslick, and likely for other musical "), _c('em', [_vm._v("Kenner,")]), _vm._v(" the music’s reticence to disclose all its technical intricacies opens the possibility for a process of continual discovery.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("We thus might distinguish two general ways in which artifice, expression, and intense personal engagement can interact as a player comes to know a Brahms intermezzo. On the one hand, the music’s expressive contours often correspond with the learned processes occurring beneath the surface—but without drawing attention to those processes. In this way, not only does the music seem sonically to interpret its own formal values, it also draws the player into those very moments at which formal processes are at their richest. This interaction can lead to what we might call a “covert assimilation” of the music’s learned features in which the player, though perhaps unaware of the music’s artifice, is drawn closest to the work—and identifies with it most deeply—at its moments of greatest technical achievement. Second, the music’s submerged formal complexity makes it an inexhaustible source of discovery and consistently renewed interaction, especially for musical "), _c('em', [_vm._v("Kenner")]), _vm._v(" (such as Elisabet von Herzogenberg, Clara Schumann, Eduard Hanslick, or modern players of an analytical bent). For such musicians, the affective physiognomy and learned procedures in Brahms’s intermezzi offer quasi-independent modes of aesthetic contemplation, which nevertheless can become entangled in striking and highly variable ways from piece to piece. The dialectical circulation between assimilation and reflective distance can become especially intense for such players.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To further flesh out these ideas, we will now turn to a survey of several additional intermezzi, exploring the pieces’ diverse aesthetic affordances—technical and sonic—and tracing their interactions. These analytical vignettes will also bring to light certain striking corporeal aspects of Brahms’s music—moments in which the composer asks the player to enact bodily motions that correspond in some way to the music’s underlying technical processes, thus constructing a learned self in a quite literal sense.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _vm._v("Intermezzo in E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Major, op. 117, no. 1")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Intermezzo in E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Major, op. 117, no. 1, at first seems like one of the most artless of the intermezzi: folklike, pastoral, and direct, with a simple homophonic accompaniment at the outset that has been likened to the strumming of a guitar."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn35"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn35"
    }
  }, [_vm._v("35")])]), _vm._v(" The folklike character is reinforced by the piece’s epigraph, taken from a Scottish lullaby in Herder’s "), _c('em', [_vm._v("Stimmen der Völker in Liedern.")]), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn36"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn36"
    }
  }, [_vm._v("36")])]), _vm._v(" Brahms referred to all three pieces of op. 117 as “lullabies of my sorrows” ("), _c('em', [_vm._v("Wiegenlieder meiner Schmerzen")]), _vm._v("), but the E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" is the most explicit "), _c('em', [_vm._v("Wiegenlied")]), _vm._v(" of the set."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn37"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn37"
    }
  }, [_vm._v("37")])]), _vm._v(" (We will return to the "), _c('em', [_vm._v("Schmerzen")]), _vm._v(" below.) Example 2.4 shows the work’s A section (mm. 1–20). A host of musical details signify maternal song: gently rocking "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common68.jpg"
    }
  }), _vm._v(" meter, simple diatonic melody, slow harmonic rhythm, and enveloping E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" octaves tolling in multiple registers. The lulling E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v("s create a sonic field that enfolds the melody in tonic security. This sonic image resonates with contemporary German ideas about the lullaby and the mother’s voice. As Karen Bottge has observed (drawing on ideas of Friedrich Kittler), the maternal voice was often described in nineteenth-century German writings on parenting as creating an “aural envelope” that surrounds the infant in a field of sensuous pleasure."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn38"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn38"
    }
  }, [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "image1"
  }, [_c('a', {
    attrs: {
      "id": "example2.4"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f032-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f032-02.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.4. Intermezzo in E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Major, op. 117, no. 1, A section (mm. 1–20).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But there is a curious textural inversion in the intermezzo: it is the lullaby melody itself that is enveloped by the E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v("s. In other words, the melody that would be associated with the mother’s voice finds itself in the middle of the texture, comfortably enveloped, in the place reserved for the child. One can thus detect a blurring of identities between mother and child, or a confusion of boundaries."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn39"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn39"
    }
  }, [_vm._v("39")])]), _vm._v(" Later events reinforce this understanding, as the melody gradually saturates the texture, further breaking down any clear distinction between enveloper and enveloped. Voices that had been part of the static homophonic field gradually become melodically individuated: with the pickup to m. 5, the right-hand octaves are set in motion melodically (along with the “upper” tenor voice, in sixths below), while the bass and “lower” tenor voice begin to move with the pickup to m. 7. As shown in Example 2.5, the bass octaves present an exact transposition of the opening of the melody in the dominant. The now-mobile upper voices further echo this melody in a free canon, as shown. Thus, each stratum picks up portions of the lullaby melody—each voice at once asserts its own melodic identity and at the same time fails to distinguish itself from the other linear strata, which have the same melodic material. The lullaby resonates in all registers of the sonic envelope, creating a tension between melodic differentiation (or independence) and homogeneity. As suggestive as such ideas are for a Lacanian reading of the piece, such an interpretation would take us too far afield from the present topic. What is most pertinent to our subject is that the idea of "), _c('em', [_vm._v("canon")]), _vm._v(" arises very naturally in this expressive context, as a web of melodic lines that are at once independent yet highly similar—one might even say genetically linked.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.5"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f033-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.5. Free canon in mm. 6–8 of the Intermezzo in E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Major, op. 117, no. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }), _vm._v("The canon in Example 2.5 occurs at the first expressive crest in the piece, the music swelling dynamically and reaching an affective peak with the entry of the "), _c('em', [_vm._v("comes.")]), _vm._v(" The intersection between expressive apex and learned device recalls the A-major intermezzo. While this initial canon is quite free rhythmically, two later passages show a progression toward increasingly exact canonic activity. Example 2.6 isolates the two passages, which sound at parallel moments in the A and A’ sections. As shown in Examples 2.6a and b, the lullaby melody enters into a canon with itself in m. 13 at the temporal interval of two eighths. This two-eighths span sets off a hemiola in the left hand, the first departure from the lulling "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/music1.jpg"
    }
  }), _vm._v(" rhythm in the piece. The canon grows out of the right hand’s E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" octaves: the "), _c('em', [_vm._v("dux,")]), _vm._v(" departing from the upper E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(", seems to peel away the "), _c('em', [_vm._v("comes")]), _vm._v(" from the lower E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(", further interlinking melody and homophonic background. The canon is exact until the "), _c('em', [_vm._v("comes")]), _vm._v(" begins moving in sixteenths. Examples 2.6c and d show the return of this passage in the A′ section in mm. 50–51. Now the resounding E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" is integrated into the canonic melody itself via oscillating sixteenths. The canon here is entirely literal, extending from m. 50 through the end of m. 51; it further projects vertically through nearly the entire sounding texture. The incursion of the "), _c('em', [_vm._v("comes")]), _vm._v(" into the bass clef in m. 51 involves yet another stratum in the canonic activity, mixing melodic tones with the arpeggiated tenor-register harmonies.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Daniel Beller-McKenna hears such learned processes as opening up a distance between the listener or player and the pastoral lullaby:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("With each passing device that Brahms applies to the pastoral opening, the folk character of the initial idea is objectified and thus placed at a distance. . . . The folk quality of the opening is rendered not so much an affectation as a memory of something cherished from afar, something over which the ego can reminisce and contemplate—even brood—but which has become distant."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn40"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn40"
    }
  }, [_vm._v("40")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Though Beller-McKenna does not explicitly mention the canons, they can certainly be heard to contribute to this process, opening up a subtle ironic distance from the simple, folklike homophony of the opening. But there is no crisp separation. The canons at once seem to grow naturally out of the music—as ineluctable products of its descending diatonic lullaby—and at the same time "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }), _vm._v("suggest a surfeit of technique that is not in keeping with the piece’s initial pastoral premise."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn41"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn41"
    }
  }, [_vm._v("41")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The high register of both canons in Example 2.6 creates the effect of a shimmering cascade, but the canonic activity also gives the passages a subtly mechanical sound, almost like a music box."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn42"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn42"
    }
  }, [_vm._v("42")])]), _vm._v(" This suggests a vaguely impersonal character, or perhaps even an affective detachment at these moments. That detachment contrasts strongly with the music that follows—both canons lead into one of the most arresting harmonies in the piece: the vii"), _c('sup', [_vm._v("Ø7")]), _vm._v("/V in mm. 15 and 52. The registral contrast and the A bass note—a tritone away from the still-resounding E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" octaves—suggest a subjective presence beneath the pastoral surface (and its curious canons), a presence that only emerges fully in the minor-key B section. Dillon Parmer reads this as the subjective presence of the “unhappy mother” in the lullaby, which Herder titles “Wiegenlied einer unglücklichen Mutter.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn43"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn43"
    }
  }, [_vm._v("43")])]), _vm._v(" In such a reading, the "), _c('em', [_vm._v("Schmerzen")]), _vm._v(" of Brahms’s description reside in this subject, as the fourth line of the lullaby suggests:")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "example2.6"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f035-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.6. Increasingly exact canons in mm. 13–14 and 50–51 of the Intermezzo in E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Major, op. 117, no. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "poem1"
  }, [_c('a', {
    attrs: {
      "id": "page_36"
    }
  }), _vm._v("Schlaf sanft mein Kind, schlaf sanft und schön!")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("Mich dauerts sehr, dich weinen sehn.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("Und schläfst du sanft, bin ich so froh,")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("Und wimmerst du—das schmerzt mich so!")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("(Sleep softly, my child, sleep softly and sweetly!")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("It troubles me so to see you cry.")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("And if you slept softly, I would be so happy,")]), _vm._v(" "), _c('p', {
    staticClass: "poem"
  }, [_vm._v("But your sobbing pains me so!)")]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("However we choose to read the intermezzo—a literal mapping of music onto lullaby text is not the only possible interpretation—the innocent, even mechanical canons seem to act as a foil for this brooding, subjective presence; they grow out of the lullaby and reveal it as a repetitive process that covers deeper emotions like a carapace. As such, and perhaps paradoxically, they are among the intermezzo’s most memorable moments—sonic emblems of its enigmatic expressive world.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("Intermezzo in B Minor, op. 119, no. 1")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Commentaries on the Intermezzo in B Minor, op. 119, no. 1, almost always begin—and all too often end—with a discussion of the famous opening arpeggios: the chains of descending thirds that conceal underlying root progressions by fifth."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn44"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn44"
    }
  }, [_vm._v("44")])]), _vm._v(" What is rarely observed is that this harmonic mist clears in m. 4 to reveal a canon. Example 2.7a shows the A section and the first measure of the B section (mm. 1–17). Example 2.7b isolates the canonic passage between soprano and bass in mm. 4–7, while 2.7c shows a longer canon in mm. 12–16, in the consequent phrase. Annotations beneath the staves in Examples 2.7b and c indicate the canonic intervals at which "), _c('em', [_vm._v("comes")]), _vm._v(" follows "), _c('em', [_vm._v("dux.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Canon plays a very different expressive role in the B-minor intermezzo than in the E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" work. While in the E"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v("-major intermezzo canon can be heard to epitomize both the innocence and routine of the lullaby (while also ironically distancing the playing subject from that innocence), the canons in the B-minor intermezzo suggest a rational control and purposefulness that contrast with the hazy harmonic wash of the opening. The strictness of procedure in these bars—the canons are all literal, with the only license being a shift in interval of imitation between phrases—recalls Brahms’s comments in his exchange with Clara about compositional license and rule. Following the descending third-chains—with their “teeming discords”—the canons are textbook examples of contrapuntal decorum; one is reminded in this connection of the etymological link between “canon” and “law.”")]), _vm._v(" "), _c('p', {
    staticClass: "image1"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.7"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f037-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f037-02.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.7. Intermezzo in B Minor, op. 119, no. 1, mm. 1-17.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_38"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.8"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f038-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.8. Submerged three-note chromatic canons in mm. 17–20 of the Intermezzo in B Minor, op. 119, no. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "example2.9"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f038-02.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.9. Canonic activity in mm. 27–35 of the Intermezzo in B Minor, op. 119, no. 1. 38 "), _c('em', [_vm._v("Steven Rings")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }), _vm._v("The canons of Examples 2.7b and c coincide with an infusion of harmonic direction and syntactical clarity into the music: they seem to act as engines that drive the work toward cadential fulfillment, as the hazy third-chains give way to purposeful canonic sequences. In the rarefied waltz of the B section, they also drive the intermezzo toward its affective climax. Example 2.8 shows three-note chromatic gestures submerged within the opening of the waltz, labeled "), _c('em', [_vm._v("a, b, b′")]), _vm._v(", and "), _c('em', [_vm._v("c.")]), _vm._v(" The first two, "), _c('em', [_vm._v("a")]), _vm._v(" and "), _c('em', [_vm._v("b,")]), _vm._v(" project an exact canon at the pitch interval of a ninth and the temporal interval of a quarter note. Segments "), _c('em', [_vm._v("b")]), _vm._v("′ and "), _c('em', [_vm._v("c")]), _vm._v(" are in a looser canonic relationship as regards rhythm; "), _c('em', [_vm._v("b′")]), _vm._v(" is so labeled because it contains the same pitch-class sequence as "), _c('em', [_vm._v("b.")]), _vm._v(" At the repeat of the waltz theme in m. 31, canonic activity redoubles and is paired with inversional patterning. As shown in Examples 2.9a and b, the canonic entries now extend to four-note gestures, beamed in 2.9b and labeled "), _c('em', [_vm._v("w, x, y,")]), _vm._v(" and "), _c('em', [_vm._v("z.")]), _vm._v(" Of these, "), _c('em', [_vm._v("x, y,")]), _vm._v(" and "), _c('em', [_vm._v("z")]), _vm._v(" are chromatic tetrachords; "), _c('em', [_vm._v("w")]), _vm._v(" is almost chromatic. Statements of "), _c('em', [_vm._v("c, a,")]), _vm._v(" and "), _c('em', [_vm._v("b")]), _vm._v(" also sound in mm. 30–33, as shown by the brackets.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The canonic activity rubs ironically against the B section’s underlying waltz. As Roger Moseley has observed, the left hand in the opening bars of the B section could easily be recomposed as an oom-pah-pah bass pattern, revealing the closeness of the music’s melodic and rhythmic idiom to Brahms’s op. 39 and the "), _c('em', [_vm._v("Liebeslieder,")]), _vm._v(" opp. 52 and 65."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn45"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn45"
    }
  }, [_vm._v("45")])]), _vm._v(" But the chromatically surging canonic voices defamiliarize this waltz, saturating the texture with overdeter-mined contrapuntal imitation. Here the fact that the artifice is buried deep within the texture seems to be of crucial expressive significance: as the four-note beamed gestures surge upward and downward, the music projects a sense of energy welling up underneath the elegant surface of the dance. That energy breaks the surface with the statement of tetrachord "), _c('em', [_vm._v("z")]), _vm._v(" in octaves in mm. 33–34. As in the A-major intermezzo, this is a moment of both technical and expressive fulfillment. Brahms underscores the importance of the passage by repeating it immediately in mm. 37–39, now embellishing the "), _c('em', [_vm._v("z")]), _vm._v(" tetrachord with elaborate sixteenth-note triplets. This passage is the movement’s dynamic plateau—a sustained "), _c('em', [_vm._v("forte")]), _vm._v(" from m. 37 to the "), _c('em', [_vm._v("diminuendo")]), _vm._v(" hairpin in m. 39.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What is fulfilled here? Tetrachord "), _c('em', [_vm._v("z")]), _vm._v(" can be heard in one sense as a “corrected” version of tetrachord "), _c('em', [_vm._v("w.")]), _vm._v(" The latter has the local dominant A as its lowest note, but it is not fully chromatic; it is thus an inexact inversion of "), _c('em', [_vm._v("x,")]), _vm._v(" against which it "), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _vm._v("sounds in the liquidation of mm. 27–30. Tetrachord "), _c('em', [_vm._v("z")]), _vm._v(" also has the dominant A as its lowest note, but it "), _c('em', [_vm._v("is")]), _vm._v(" a fully chromatic tetrachord. It is an exact inversion of tetrachord "), _c('em', [_vm._v("y,")]), _vm._v(" which precedes it canonically in mm. 31–32; "), _c('em', [_vm._v("y")]), _vm._v(" is, for its part, an exact inversion of "), _c('em', [_vm._v("x.")]), _vm._v(" The transformational network in Example 2.9c summarizes these relationships. As it indicates, "), _c('em', [_vm._v("x")]), _vm._v(" inverts into "), _c('em', [_vm._v("y")]), _vm._v(" via the inversion that maps A"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v(" onto D; "), _c('em', [_vm._v("y")]), _vm._v(" then inverts into "), _c('em', [_vm._v("z")]), _vm._v(" by the inversion that maps A onto D. And "), _c('em', [_vm._v("z")]), _vm._v(" is further the T"), _c('sub', [_vm._v("11")]), _vm._v(" transposition of "), _c('em', [_vm._v("x.")]), _vm._v(" T"), _c('sub', [_vm._v("11")]), _vm._v(" moves the A"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v(" at the bottom of "), _c('em', [_vm._v("x")]), _vm._v(" down to the A at the bottom of "), _c('em', [_vm._v("z,")]), _vm._v(" presenting a diatonic correction of sorts and providing the first chromatic tetrachord that begins on the local dominant.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But these transformations result in a new chromatic inflection: the C"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music004.jpg"
    }
  }), _vm._v(" that concludes motive "), _c('em', [_vm._v("z.")]), _vm._v(" This note turns back the motive’s ascending chromatic energy, bending it now in the direction of the "), _c('em', [_vm._v("sub")]), _vm._v("dominant. This is the first flatward motion in the piece. The A section proceeded from the tonic (two sharps) to the minor dominant (three sharps), while the first phrase of the B section was shot through with ascending chromatics, driven by the three-note motives shown in Example 2.8b. In mm. 21–24, the entire musical texture is draped onto these chromatics, resulting in a metrically unstable passage of ascending parallel "), _c('img', {
    staticClass: "block",
    attrs: {
      "alt": "Image",
      "src": "images/common63.jpg"
    }
  }), _vm._v(" chords: D major, D minor, E minor, E minor(!), F minor, G minor. After this ascent, the tilt in the direction of the subdominant at the end of tetrachord "), _c('em', [_vm._v("z")]), _vm._v(" is powerfully affecting: the flatward easing of tension is held in check by the upward thrust of the gesture, the increased dynamics (to the "), _c('em', [_vm._v("forte")]), _vm._v(" plateau of mm. 37–39), and the thick, octave-doubled texture. These competing forces at once give the "), _c('em', [_vm._v("z")]), _vm._v(" motive a rhetorical heft and a sense of valedictory turning back. As Robert Hatten and Michael Klein have suggested, in much tonal music the dominant can have the quality of pointing to the future (“time as experienced rushes forward,” as Klein puts it), while the subdominant can seem instead to gesture toward the past (“time as experienced turns back”)."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn46"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn46"
    }
  }, [_vm._v("46")])]), _vm._v(" Klein’s comments seem especially apt for Brahms’s subdominant in mm. 34 and 38, as the music here seems to double back on itself, giving this, the emotional apex of the movement, a strongly retrospective charge.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In the retransition (mm. 43–46) Brahms asks the pianist to cross hands repeatedly in executing overlapping four-note descending third-chains. This is merely one vivid instance of a common procedure in the intermezzi, in which the composer asks the performer to execute a motion that in some sense physically manifests the technical idea at work in the music—in this passage, the technique of register transfer through reaching over (loosely in Schenker’s sense). Another vivid instance of this phenomenon occurs in the final chord of the intermezzo, shown in Example 2.10. As indicated on the right-hand side of the example, Brahms easily could have notated the chord as two close position B-minor triads, one in each hand. Instead, he chose to notate it as shown on the left-hand side of the example, with each hand playing characteristically Brahmsian gapped sonorities. This not only causes the pianist’s thumbs to overlap but places the hands in an explicitly inversional configuration. Thus, the inversional patterning evident at the work’s climax (Example 2.9 and following) finds its way into the pianist’s hands for the final sonority.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_41"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.10"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f041-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.10. The final chord of op. 119, no. 1.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("Intermezzi in E Minor, op. 116, no. 5, and B"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Minor, op. 117, no. 2")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The concluding observations about op. 119, no. 1, reveal an additional way in which Brahms constructs the experience of the playing subject in his late intermezzi: the artifice embedded within certain passages is made physically palpable by being mapped into the performer’s hands. Through fingerings, voicings, and patterns of figuration, Brahms choreographs the player’s movements in ways that interact with and project the work’s learned processes. Most notably, he often exploits the natural inversional relationship of the pianist’s hands at the keyboard, with each hand presenting a mirror image of the other as regards the ordering of thumb and fingers. By exploiting this somatic configuration, Brahms can map inversional activity in the music directly onto the performer’s body."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn47"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn47"
    }
  }, [_vm._v("47")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Such a mapping is especially evident in the inversional patterning of the Intermezzi in E Minor, op. 116, no. 5, and B"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Minor, op. 117, no. 2. In the former piece, the pianist’s hands overlap while executing mirroring inversional gestures. Example 2.11 shows bars 1–2. The voicing of the first sonority matches that of the concluding sonority of op. 119, no. 1. The fingerings, which are present in the original 1892 Simrock edition, make the mirroring relationship of the hands explicit. In a letter dated 13 October 1892, Clara commented on this very aspect of the piece:")]), _vm._v(" "), _c('p', {
    staticClass: "blockquote"
  }, [_vm._v("I should never like to hear the E minor with the single notes only, because it is just the position of the hands, one on top of the other, that has a peculiar charm and sounds quite different."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn48"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.11"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f042-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.11. Intermezzo in E Minor, op. 116, no. 5, mm. 1–2.")]), _vm._v(" "), _c('p', {
    staticClass: "image1"
  }, [_c('a', {
    attrs: {
      "id": "example2.12"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f042-02.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.12. Gestures "), _c('em', [_vm._v("x")]), _vm._v(" and "), _c('em', [_vm._v("x"), _c('sup', [_vm._v("-1")])]), _vm._v(" in the Intermezzo in B"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Minor, op. 117, no. 2.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_43"
    }
  }), _vm._v("The Intermezzo in B"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" Minor, op. 117, no. 2, exhibits equally pervasive inversional structuring in the gestures given to the pianist’s hands. Example 2.12 explores the interaction of two gestures in the piece: the persistent four-note descending figure played by the right hand, labeled "), _c('em', [_vm._v("x,")]), _vm._v(" and its inversion, played by the left, "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(". Example 2.12a isolates these gestures in a staffless space. The slurs in both gestures project a melodic dyad, usually a diatonic or chromatic step. The beamed thirty-second notes that follow are a gapped arpeggio. Example 2.12b shows the configuration of "), _c('em', [_vm._v("x")]), _vm._v(" and "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(" in the piece’s opening measures. Here the gestural rhythm "), _c('em', [_vm._v("x")]), _vm._v("–"), _c('em', [_vm._v("x")]), _vm._v("–"), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(" or rh–rh–lh is established.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Throughout the A section of the piece, the right hand leads with two or sometimes three statements of "), _c('em', [_vm._v("x,")]), _vm._v(" while the left hand responds with one statement of "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(". In the retransition, however—shown in 2.12c—the left hand leads. It begins with two statements of "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(", which are imitated canonically by "), _c('em', [_vm._v("x")]), _vm._v(" in the right hand, leading to a stretto pile-up in mm. 40–42. In mm. 43–44 (and 45–46, not shown) the tight alternation and overlap of the two motives is liquidated by extended, single-direction arpeggios: "), _c('em', [_vm._v("x")]), _vm._v("–"), _c('em', [_vm._v("x")]), _vm._v("–"), _c('em', [_vm._v("x")]), _vm._v(" followed by "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v("–"), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v("–"), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(". Here, however, the hands for the first time abandon their allegiance to a single "), _c('em', [_vm._v("x")]), _vm._v("-form: Brahms’s stemming suggests an alternation of hands within each arpeggio. This new pattern leads to a larger-scale inversional symmetry, as the middle "), _c('em', [_vm._v("x-")]), _vm._v("form in each unidirectional arpeggio is played by the “wrong” hand. As indicated on the example, the descending "), _c('em', [_vm._v("x")]), _vm._v("–"), _c('em', [_vm._v("x")]), _vm._v("– "), _c('em', [_vm._v("x")]), _vm._v(" arpeggio is performed rh–lh–rh, while the ascending "), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v("–"), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v("–"), _c('em', [_vm._v("x")]), _c('sup', [_vm._v("-1")]), _vm._v(" is realized by the "), _c('em', [_vm._v("inverse")]), _vm._v(" pattern lh–rh–lh. Though such relationships appear recondite on paper, they have a somatic immediacy for the player, who bodily manifests the music’s inversional structures.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("Intermezzo in F Minor, op. 118, no. 4")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Intermezzo in F Minor, op. 118, no. 4, initially seems as though it will be concerned with the same issues of inversional patterning between the hands. Example 2.13a shows mm. 1–14. The triplet figures in the hands present an exact inversional relationship in their initial gestures, suggesting that the piece might be an essay on canon in inversion. But the inversions soon become increasingly free, until they are seemingly displaced by an explicit (and exact) "), _c('em', [_vm._v("non")]), _vm._v("inverted canon at the cadence in mm. 7 (beat 2) through 12. The replacement of inverted with noninverted canon is only “seeming” because this noninverted canon has in fact been sounding all along, as shown in Example 2.13b. It begins with the accented long notes at the work’s opening. The player’s (and listener’s) attention is not drawn to these notes at the outset, however, as the triplet activity occupies center stage. But the gradual emergence of the canon into the sonic foreground reveals a process that has already been under way.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_44"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.13"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f044-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f044-02.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.13. Intermezzo in F Minor, op. 118, no. 4, mm. 1–14.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_45"
    }
  }), _c('a', {
    attrs: {
      "id": "example2.14"
    }
  }), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/f045-01.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "caption"
  }, [_vm._v("Example 2.14. Intermezzo in F Minor, op. 118, no. 4, mm. 52–67.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The submerged nature of the canon in mm. 1–11 seems bound up with the piece’s agitated "), _c('em', [_vm._v("Stimmung.")]), _vm._v(" It is as if the canonic activity is suppressed in these bars, or kept under a lid. It is only when that “lid” is removed, and the canon can emerge clearly in mm. 7–12, that the pressure in the piece eases slightly and the music is able to cadence. This sense of release is even more evident in the work’s B section: here all noncanonic material disappears, and the hands alternate serenely in A"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v(" major. Example 2.14 shows the first two systems of this section (mm. 52–67). As in the A section, the canon is at the octave, and the temporal interval is one quarter note. While it is the mirror symmetry of the player’s hands that Brahms exploits in the E-minor and B"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music001.jpg"
    }
  }), _vm._v("-minor intermezzi, here it is the translational symmetry of the player’s arms—their role in moving the hands right and left along the horizontal plane of the keyboard. As in the A-major intermezzo, there is a remarkable congruence in Example 2.14 between technical process and affect, both of which exhibit a placid simplicity. This congruence is matched by the serene process of the player’s hands in the F-minor intermezzo—indeed, the canon is even more evident to the player’s body than it is to the listener’s ear.")]), _vm._v(" "), _c('h4', {
    staticClass: "head1"
  }, [_vm._v("The Learned Self as Modern Analyst")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("I have proposed that artifice and affect interact in telling ways in Brahms’s intermezzi, with the music’s affective stratum seeming to "), _c('em', [_vm._v("value")]), _vm._v(" its technical processes sonically. I have also proposed that these works engender certain modes of musical engagement, in which the music’s learned values are assimilated by "), _c('a', {
    attrs: {
      "id": "page_46"
    }
  }), _vm._v("the player—to varying degrees and in diverse ways—through affective identification, cognitive reflection, somatic enactment, or (most likely) some combination of the three. Though my focus has been on the experience of the performing subject, these observations also have much to tell us about the perennial appeal of these works to music analysts.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The habitus of the professional music analyst has its historical, modernist roots in a tradition that leads directly back to Brahms via Schoenberg. It thus makes sense that the way of being with music encouraged by Brahms’s works—and by Brahms’s circle—would transfer, through a sort of historical transitivity, to the practices of the modern analyst. Though published analyses and conference papers have a decidedly social aspect, the analytical act itself is often a highly solitary pursuit, and a deeply inward one. Modern music theory as a research discipline is thus closely bound up with the "), _c('em', [_vm._v("innig")]), _vm._v(" musical behavior that we witness in the descriptions of Elisabet von Herzogenberg and Clara Schumann as they come to know, and assimilate, Brahms’s music.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("While the solitary pursuit of analysis has come in for much criticism of late, the central place of Brahms’s intermezzi in the analytical tradition might help us better understand its attractions. For it is not that analysts keep returning to these pieces "), _c('em', [_vm._v("solely")]), _vm._v(" because of their elegant formal properties. It is also surely because of the captivating sound of this music, and the special ways in which that sound seems to move in similar motion with those properties. The pleasures of the analytical act reside not only in the elegance of the relationships discovered or in the personal satisfaction one has in discovering them—though those are undoubtedly important—but also in the ways in which formal relationships emerge into consciousness when one is in direct contact with the music’s sonic materiality. Contra Vladimir Jankélévitch, Carolyn Abbate, and others, there can be something deeply hedonistic about this pursuit."), _c('sup', [_c('a', {
    attrs: {
      "id": "ich02fn49"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch02fn49"
    }
  }, [_vm._v("49")])]), _vm._v(" Analysis entices in part because of the diversity of aesthetic engagements it affords—from the formal to the physically sensuous—and the potential that it holds for illuminating their myriad interactions. To judge from the intermezzi, such interactions seem to have fascinated Brahms as well.")]), _vm._v(" "), _c('h3', {
    staticClass: "ct-da1"
  }, [_vm._v("N"), _c('span', {
    staticClass: "small"
  }, [_vm._v("OTES")])]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn1"
    }
  }, [_vm._v("1.")]), _vm._v(" The diatonic inversion takes place within an A-major gamut. While the inversion here is diatonic (or tonal), we will soon observe instances of chromatic (or real) inversion in the vicinity of Example 2.1b. For a different technical perspective on these inversions—and on other aspects of this intermezzo—see Steven Rings, "), _c('em', [_vm._v("Tonality and Transformation")]), _vm._v(" (New York: Oxford University Press, 2011), 185–202.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "page_47"
    }
  }), _c('a', {
    attrs: {
      "id": "ch02fn2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn2"
    }
  }, [_vm._v("2.")]), _vm._v(" Schenker hears the B in the upbeat to the first measure as an incomplete passing tone within an implicit third progression C"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v("–B–(A), a hearing that I find persuasive. As he notes, in each statement the motive consistently “leaps off” ("), _c('em', [_vm._v("springt ab")]), _vm._v(") of this "), _c('em', [_vm._v("Zug")]), _vm._v(" as it crosses the bar line. See Schenker’s unpublished comments on the piece (from 1914) in the Ernst Oster Collection (New York Public Library), folder 34, item 29. Folder 34 has been called the “Brahms folder” by researchers. For a general discussion of its contents, see Allen Cadwallader and William Pastille, “Schenker’s Unpublished Work with the Music of Johannes Brahms,” in "), _c('em', [_vm._v("Schenker Studies")]), _vm._v(" 2, ed. Carl Schachter and Hedi Siegel (Cambridge: Cambridge University Press, 1999), 26–46.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn3"
    }
  }, [_vm._v("3.")]), _vm._v(" As Schenker notes, this seventh leap ("), _c('em', [_vm._v("Septsprung")]), _vm._v(") yields the tone A, the tone that “had to come” after B according to the “law of the passing tone” ("), _c('em', [_vm._v("Durchgangsgesetz")]), _vm._v("). Nevertheless, the passing B is not resolved “in the strict sense” ("), _c('em', [_vm._v("im strengen Sinne")]), _vm._v(") due to the registral displacement of the leap. Oster Collection, folder 34, item 29.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn4"
    }
  }, [_vm._v("4.")]), _vm._v(" The high A becomes harmonically dissonant as well when the leap reappears in the parallel consequent phrase: the A of m. 6 sits atop a B"), _c('sup', [_vm._v("7")]), _vm._v(" chord in first inversion, forming a tritone with the D"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v(" bass.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn5"
    }
  }, [_vm._v("5.")]), _vm._v(" Charles Rosen, "), _c('em', [_vm._v("Piano Notes: The World of the Pianist")]), _vm._v(" (New York: Simon and Schuster, 2004), 9.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn6"
    }
  }, [_vm._v("6.")]), _vm._v(" A massive subdominant arrival, discussed below, precedes the music shown in Example 2.3a, reinforcing the sense of bass-note D as structural in these measures, with C"), _c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/music002.jpg"
    }
  }), _vm._v(" as its neighbor. For a detailed Schenkerian reading along these lines, see Rings, "), _c('em', [_vm._v("Tonality and Transformation,")]), _vm._v(" 200, Fig. 6.13. For alternative Schenkerian readings, see Allen Cadwallader, “Foreground Motivic Ambiguity: Its Clarification at Middleground Levels in Selected Late Piano Pieces of Johannes Brahms,” "), _c('em', [_vm._v("Music Analysis")]), _vm._v(" 7/1 (1988): 70, Ex. 9; and Eric Wen, “Bass-line Articulations of the "), _c('em', [_vm._v("Urlinie,")]), _vm._v("” in Schachter and Siegel, "), _c('em', [_vm._v("Schenker Studies")]), _vm._v(" 2, 294–97, Ex. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn7"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn7"
    }
  }, [_vm._v("7.")]), _vm._v(" The local harmonies accompanying the chromatic (real) inversion in mm. 33–34 have a suggestive dualist character: minor subdominant inverts to major tonic. This delivers on a dualist implication already evident in Examples 2.2c and d. The inversional sketches of those examples have a mildly dualist appearance on the page, with the neighboring third motions surrounding a central A above and below. Appropriately, the “under thirds” of 2.2d align with the heavily subdominant transformation of the inverted motive. The harmonic context of 2.2d is not properly dualist, of course, as the D harmony is major, not minor. But in mm. 33–34 the true minor subdominant does sound, setting up a genuine dualist flip around A, in concert with the flurry of other inversional activity already explored.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn8"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn8"
    }
  }, [_vm._v("8.")]), _vm._v(" Margaret Notley, "), _c('em', [_vm._v("Lateness and Brahms: Music and Culture in the Twilight of Viennese Liberalism")]), _vm._v(" (Oxford: Oxford University Press, 2007), 34.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn9"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn9"
    }
  }, [_vm._v("9.")]), _vm._v(" Christian Martin Schmidt, "), _c('em', [_vm._v("Brahms und seine Zeit")]), _vm._v(" (Laaber: Laaber Verlag, 1998), 89, cited and translated in Notley, "), _c('em', [_vm._v("Lateness and Brahms,")]), _vm._v(" 40–41. See also Carl Dahlhaus, "), _c('em', [_vm._v("Nineteenth-Century Music,")]), _vm._v(" trans. J. Bradford Robinson (Berkeley: University of California Press, 1989), 258–61.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn10"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn10"
    }
  }, [_vm._v("10.")]), _vm._v(" See Notley, "), _c('em', [_vm._v("Lateness and Brahms,")]), _vm._v(" 2ff. David S. Luft offers a particularly cogent discussion of the constitutive roles of concepts such as rationality, individuality, inwardness, reflexivity, and so on in Viennese Liberalism in his book "), _c('em', [_vm._v("Eros and Inwardness in Vienna: Weininger, Musil, Doderer")]), _vm._v(" (Chicago: University of Chicago Press, 2003), see esp. 14–22. The classic accounts of the decline of Viennese Liberalism in the decades leading up to the fin de siècle are Carl Schorske, "), _c('em', [_vm._v("Fin-de-siècle Vienna: Politics and Culture")]), _vm._v(" (New York: Knopf, 1980); and John Boyer, "), _c('em', [_vm._v("Political Radicalism in Late Imperial Vienna: Origins of the Christian Social Movement, 1848–1897")]), _vm._v(" (Chicago: University of Chicago Press, 1981). For a collection of recent historical essays that challenge the "), _c('a', {
    attrs: {
      "id": "page_48"
    }
  }), _vm._v("accounts of Schorske and Boyer, see Steven Beller, ed., "), _c('em', [_vm._v("Rethinking Vienna 1900")]), _vm._v(" (New York: Berghahn, 2001).")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn11"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn11"
    }
  }, [_vm._v("11.")]), _vm._v(" Notley primarily explores chamber music in her discussion of Brahmsian artifice; she only mentions two intermezzi in passing (op. 116, no. 5, and op. 118, no. 4; see "), _c('em', [_vm._v("Lateness in Brahms,")]), _vm._v(" 59). Though in this chapter I focus on the works that Brahms titled “Intermezzo,” my comments will be generally applicable to all Brahms’s late piano works in opp. 116–19 as well as the earlier set of op. 76. As Elisabet von Herzogenberg noted in an 1880 letter to Brahms, the titles for such small pieces—Intermezzo, Romance, Capriccio, Ballade, Rhapsody—were largely arbitrary: “It is practically a characteristic of these various designations that they have lost their true characteristics through application, so that they can be used for this or that at will.” Max Kalbeck, ed., and Hannah Bryant, trans., "), _c('em', [_vm._v("Johannes Brahms: The Herzogenberg Correspondence")]), _vm._v(" (New York: E. P. Dutton, 1909), 99.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn12"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn12"
    }
  }, [_vm._v("12.")]), _vm._v(" Berthold Litzmann, "), _c('em', [_vm._v("Clara Schumann: Ein Künstlerleben")]), _vm._v(" (Leipzig: Breitkopf und Härtel, 1923), 3:563, my translation. Jan Swafford speculates that Brahms may have composed the late piano pieces “to sustain Clara” in her declining health. See his "), _c('em', [_vm._v("Johannes Brahms: A Biography")]), _vm._v(" (New York: Vintage, 1997), 602.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn13"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn13"
    }
  }, [_vm._v("13.")]), _vm._v(" Berthold Litzmann, ed., "), _c('em', [_vm._v("Letters of Clara Schumann and Johannes Brahms, 1853–1896")]), _vm._v(" (London: Edward Arnold, 1927), 2:228.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn14"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn14"
    }
  }, [_vm._v("14.")]), _vm._v(" Litzmann, "), _c('em', [_vm._v("Letters,")]), _vm._v(" 2:229.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn15"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn15"
    }
  }, [_vm._v("15.")]), _vm._v(" Litzmann, "), _c('em', [_vm._v("Letters,")]), _vm._v(" 2:229.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn16"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn16"
    }
  }, [_vm._v("16.")]), _vm._v(" In an extended discussion of the idea of melancholy in Brahms’s music, Reinhold Brinkmann draws attention to the curious mixture of the words “melancholy” and “delight” ("), _c('em', [_vm._v("Wollust")]), _vm._v(") in the May 1893 letter. See Brinkmann, "), _c('em', [_vm._v("Late Idyll: The Second Symphony of Johannes Brahms,")]), _vm._v(" trans. Peter Palmer (Cambridge, Mass.: Harvard University Press, 1995), 131. See also Jan Brachmann’s discussion of Brahmsian melancholy in "), _c('em', [_vm._v("Kunst, Religion, Krise: Der Fall Brahms")]), _vm._v(" (Kassel: Bärenreiter, 2003), 264ff. Daniel Beller-McKenna explores the idea of melancholy in the intermezzi in “Reminiscence in Brahms’s Late Intermezzi,” "), _c('em', [_vm._v("American Brahms Society Newsletter")]), _vm._v(" 22/2 (2004): 6–9.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn17"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn17"
    }
  }, [_vm._v("17.")]), _vm._v(" Eduard Hanslick, "), _c('em', [_vm._v("Fünf Jahre Musik [1891–1895]")]), _vm._v(" (Berlin: Allgemeiner Verein für deutschen Literatur, 1896), 258–59, my translation.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn18"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn18"
    }
  }, [_vm._v("18.")]), _vm._v(" It is apposite in this connection to note that Brahms only once performed any of the late piano pieces in a semiformal concert: at the "), _c('em', [_vm._v("Tonkünstlerverein")]), _vm._v(" on 31 October 1893. All other known performances were in the homes of friends (at the Fellingers’ in Vienna and the Villa of Miller zu Aichholz in Gmunden). See Renate and Kurt Hoffman, "), _c('em', [_vm._v("Johannes Brahms als Pianist und Dirigent: Chronologie seines Werkes als Interpret")]), _vm._v(" (Tutzing: Hans Schneider, 2006). For a brief description of the "), _c('em', [_vm._v("Tonkünstlerverein")]), _vm._v(" event—at which Brahms evidently first played op. 118, no. 2, very poorly and then returned to play it much better—see Richard Heuberger, "), _c('em', [_vm._v("Erinnerungen an Johannes Brahms")]), _vm._v(" (Tutzing: Hans Schneider, 1971), 62. The first significant public performances of the works were those of Ignaz Brüll (reviewed by Hanslick) and Ilona Eibenschütz.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn19"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn19"
    }
  }, [_vm._v("19.")]), _vm._v(" For further accounts of the reception of the late piano works, see Imogen Fellinger, “Brahms’ Klavierstücke op. 116–119: Kompositorische Bedeutung und zeitgenössische Rezeption,” in "), _c('em', [_vm._v("Johannes Brahms: Quellen—Text—Rezeption—Interpretation: Internationaler Brahms-Kongreß, Hamburg 1997,")]), _vm._v(" ed. Friedrich Krummacher and Michael Struck (Munich: Henle, 1999), 199–210.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn20"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn20"
    }
  }, [_vm._v("20.")]), _vm._v(" Dahlhaus, "), _c('em', [_vm._v("Nineteenth-Century Music,")]), _vm._v(" 260. The passage follows an analytical discussion of Brahms’s Capriccio in G Minor, op. 116, no. 3.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn21"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn21"
    }
  }, [_vm._v("21.")]), _vm._v(" Litzmann, "), _c('em', [_vm._v("Clara Schumann,")]), _vm._v(" 3:123, emphasis added. Translation from David Brodbeck, "), _c('em', [_vm._v("Brahms: Symphony No. 1")]), _vm._v(" (Cambridge: Cambridge University Press, 1997), 10.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn22"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn22"
    }
  }, [_vm._v("22.")]), _vm._v(" Nietzsche asserts that Brahms’s music differs from Wagner’s music in degree, not in kind: “Fifty steps beyond Brahms you encounter Wagner.” Friedrich Nietzsche, "), _c('em', [_vm._v("The "), _c('a', {
    attrs: {
      "id": "page_49"
    }
  }), _vm._v("Case of Wagner,")]), _vm._v(" in "), _c('em', [_vm._v("Basic Writings of Nietzsche,")]), _vm._v(" ed. and trans. Walter Kaufmann (New York: Modern Library, 2000), 643.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn23"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn23"
    }
  }, [_vm._v("23.")]), _vm._v(" Notley, "), _c('em', [_vm._v("Lateness and Brahms,")]), _vm._v(" 57–62. See also Richard Taruskin, "), _c('em', [_vm._v("The Nineteenth Century,")]), _vm._v(" vol. 3 of "), _c('em', [_vm._v("The Oxford History of Western Music")]), _vm._v(" (Oxford: Oxford University Press, 2005), 732.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn24"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn24"
    }
  }, [_vm._v("24.")]), _vm._v(" Anecdotal evidence that I have collected indeed confirms that even highly trained musicians who know the piece well may be unaware of the inversion here.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn25"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn25"
    }
  }, [_vm._v("25.")]), _vm._v(" Ernst Rudorff, “Johannes Brahms: Erinnerungen und Betrachtungen,” "), _c('em', [_vm._v("Schweizerische Musikzeitung")]), _vm._v(" 97 (1957): 83. Cited in Brodbeck, "), _c('em', [_vm._v("Brahms: Symphony No. 1,")]), _vm._v(" 31.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn26"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn26"
    }
  }, [_vm._v("26.")]), _vm._v(" These ideas engage what Leonard Meyer calls the “rehearing problem” in music cognition: the listener’s ability to encounter the same musical work repeatedly and continue to find it deeply engaging. We are concerned here with the matter not only of rehearing but also, we might say, of “replaying.” See Meyer’s "), _c('em', [_vm._v("Music, the Arts, and Ideas: Patterns and Predictions in Twentieth-Century Culture")]), _vm._v(" (Chicago: University of Chicago Press, 1967), 42–53. See also Edward T. Cone, “Three Ways of Reading a Detective Story—or a Brahms Intermezzo,” in "), _c('em', [_vm._v("Music: A View from Delft,")]), _vm._v(" ed. Robert Morgan (Chicago: University of Chicago Press, 1989), 77–93.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn27"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn27"
    }
  }, [_vm._v("27.")]), _vm._v(" Of course, a composer’s relationship to his or her music also evolves and changes. Brahms’s own comments about the B-minor intermezzo suggest as much: after Clara has “approved” the melancholy dissonances, he can now enjoy the piece without reservation.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn28"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn28"
    }
  }, [_vm._v("28.")]), _vm._v(" Sadly, Elisabet von Herzogenberg died before Brahms completed the late piano works, so we have no record of her getting to know them at the keyboard.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn29"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn29"
    }
  }, [_vm._v("29.")]), _vm._v(" Kalbeck, "), _c('em', [_vm._v("The Herzogenberg Correspondence,")]), _vm._v(" 249. I have altered Bryant’s translation of the first sentence in the block quote to make it more faithful to the original German. See Max Kalbeck, ed., "), _c('em', [_vm._v("Johannes Brahms in Briefwechsel mit Heinrich und Elisabet von Herzogenberg")]), _vm._v(" (Berlin: Deutschen Brahms-Gesellschaft, 1921), 2:85.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn30"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn30"
    }
  }, [_vm._v("30.")]), _vm._v(" Kalbeck, "), _c('em', [_vm._v("The Herzogenberg Correspondence,")]), _vm._v(" 250, emphasis original.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn31"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn31"
    }
  }, [_vm._v("31.")]), _vm._v(" Kalbeck, "), _c('em', [_vm._v("The Herzogenberg Correspondence,")]), _vm._v(" 243–44, emphasis original. I have again altered portions of Bryant’s translation. For the original, see Kalbeck, "), _c('em', [_vm._v("Brahms in Briefwechsel,")]), _vm._v(" 2:80–81.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn32"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn32"
    }
  }, [_vm._v("32.")]), _vm._v(" Also fascinating is the contrast between Elisabet’s experience of the piece and that of her sister in a passage that has echoes of Mozart’s famous comments to his father about "), _c('em', [_vm._v("Kenner")]), _vm._v(" and "), _c('em', [_vm._v("Nichtkenner.")]), _vm._v(" Elisabet’s sister “simply enjoys what she hears,” unaware of the music’s technical complexities. Elisabet, however, needs to work to arrive at a similar stage of easy enjoyment. Emily Anderson, ed. and trans., "), _c('em', [_vm._v("The Letters of Mozart and His Family,")]), _vm._v(" 3rd ed., rev. Stanley Sadie and Fiona Smart (London: Macmillan, 1985), 833, letter 476.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn33"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn33"
    }
  }, [_vm._v("33.")]), _vm._v(" Hanslick, "), _c('em', [_vm._v("Fünf Jahre Musik,")]), _vm._v(" 259, my translation.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn34"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn34"
    }
  }, [_vm._v("34.")]), _vm._v(" Other readings are possible, however, as a result of the potential cultural resonances of the mysterious word "), _c('em', [_vm._v("Eisengehalt")]), _vm._v(" (literally, “iron content”). Berthold Hoeckner, in private communication, has suggested that the term might even refer to iron in the human blood or the bones, thus carrying connotations of healthiness and long life.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn35"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn35"
    }
  }, [_vm._v("35.")]), _vm._v(" Daniel Beller-McKenna, "), _c('em', [_vm._v("Brahms and the German Spirit")]), _vm._v(" (Cambridge, Mass.: Harvard University Press, 2004), 168.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn36"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn36"
    }
  }, [_vm._v("36.")]), _vm._v(" Johann Gottfried von Herder, "), _c('em', [_vm._v("Stimmen der Völker in Liedern")]), _vm._v(" (Stuttgart and Berlin: Cotta’sche Buchhandlung Nachfolger, 1903), 37–39. George Bozarth and Dillon Parmer both advance interpretations of the intermezzo based on close readings of the lullaby text. See George Bozarth, “Brahms’s "), _c('em', [_vm._v("Lieder ohne Worte:")]), _vm._v(" The ‘Poetic’ Andantes of the Piano Sonatas,” in "), _c('em', [_vm._v("Brahms Studies: Analytical and Historical Perspectives,")]), _vm._v(" ed. George Bozarth (Oxford: Oxford University Press, 1990), 345–78; and Dillon Parmer, “Brahms and the Poetic Motto: A Hermeneutic Aid?,” "), _c('em', [_vm._v("Journal of Musicology")]), _vm._v(" 15/3 (1997): 353–89.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "page_50"
    }
  }), _c('a', {
    attrs: {
      "id": "ch02fn37"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn37"
    }
  }, [_vm._v("37.")]), _vm._v(" Rudolf von der Leyen, "), _c('em', [_vm._v("Johannes Brahms als Mensch und Freund")]), _vm._v(" (Düsseldorf: Karl Robert Langeweische, 1905), 82–83. See also Max Kalbeck, "), _c('em', [_vm._v("Johannes Brahms")]), _vm._v(" (Tutzing: Hans Schneider, 1976), 4:277.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn38"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn38"
    }
  }, [_vm._v("38.")]), _vm._v(" Karen Bottge, “Brahms’s ‘Wiegenlied’ and the Maternal Voice,” "), _c('em', [_vm._v("19th-Century Music")]), _vm._v(" 28/3 (2005): 186–87.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn39"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn39"
    }
  }, [_vm._v("39.")]), _vm._v(" Bottge offers a highly apposite discussion of the blurring of boundaries in the mother/child dyad in “Brahms’s ‘Wiegenlied,’” 187–89.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn40"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn40"
    }
  }, [_vm._v("40.")]), _vm._v(" Beller-McKenna, "), _c('em', [_vm._v("Brahms and the German Spirit,")]), _vm._v(" 169–70.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn41"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn41"
    }
  }, [_vm._v("41.")]), _vm._v(" The canons also distance the playing subject from the pastoral naiveté of the "), _c('em', [_vm._v("Wiegenlied")]), _vm._v(" in a rather literal sense. Measures 13–14 and especially 50–51 contain the only passages in an easy piece that require careful fingering and some practice to realize in tempo, forcing the pianist to step out of musical time, plan carefully, and rehearse.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn42"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn42"
    }
  }, [_vm._v("42.")]), _vm._v(" Michael Vidmar-McEwen has presented a highly suggestive account of such “music-box passages” in nineteenth-century works in his paper “Franz Schubert and the Etherealized Mechanical,” presented at the 2008 annual meeting of Music Theory Midwest, Bowling Green, Ohio, 17 May 2008.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn43"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn43"
    }
  }, [_vm._v("43.")]), _vm._v(" Parmer, “Brahms and the Poetic Motto,” 367–75. The original English title of the lullaby is “Lady Anne Bothwell’s Lament,” published in Thomas Percey’s "), _c('em', [_vm._v("Reliques of Ancient English Poetry.")])]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn44"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn44"
    }
  }, [_vm._v("44.")]), _vm._v(" One also presumes that Brahms was talking about these arpeggios—in the opening bars and in later occurrences—when he wrote Clara that the movement “teems with discords.”")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn45"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn45"
    }
  }, [_vm._v("45.")]), _vm._v(" Roger Moseley, personal communication.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn46"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn46"
    }
  }, [_vm._v("46.")]), _vm._v(" Michael Klein, “Chopin’s Fourth Ballade as Musical Narrative,” "), _c('em', [_vm._v("Music Theory Spectrum")]), _vm._v(" 26/1 (2004): 39; Robert S. Hatten, "), _c('em', [_vm._v("Musical Meaning in Beethoven: Markedness, Correlation, and Interpretation")]), _vm._v(" (Bloomington: Indiana University Press, 1994), 43; and Hatten, “Music and Tense,” in "), _c('em', [_vm._v("Semiotics around the World: Synthesis in Diversity,")]), _vm._v(" ed. Irmengard Rauch and Gerald F. Carr (Berlin: Mouton de Gruyter, 1996), 1:628.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn47"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn47"
    }
  }, [_vm._v("47.")]), _vm._v(" These ideas mark out a potentially fruitful point of contact and further exploration between Brahms studies and Elizabeth Le Guin’s concept of “carnal musicology,” as elaborated in "), _c('em', [_vm._v("Boccherini’s Body: An Essay in Carnal Musicology")]), _vm._v(" (Berkeley: University of California Press, 2006).")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn48"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn48"
    }
  }, [_vm._v("48.")]), _vm._v(" Litzmann, "), _c('em', [_vm._v("Letters,")]), _vm._v(" 2:213.")]), _vm._v(" "), _c('p', {
    staticClass: "notes1"
  }, [_c('a', {
    attrs: {
      "id": "ch02fn49"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ich02fn49"
    }
  }, [_vm._v("49.")]), _vm._v(" Vladimir Jankélévitch and Carolyn Abbate both detect in analysis a desire to flee music’s pleasures. See Vladimir Jankélévitch, "), _c('em', [_vm._v("Music and the Ineffable,")]), _vm._v(" trans. Carolyn Abbate (Princeton, N.J.: Princeton University Press, 2003); and Carolyn Abbate, “Music—Drastic or Gnostic?,” "), _c('em', [_vm._v("Critical Inquiry")]), _vm._v(" 30 (2004): 505–36. For further discussion, see Steven Rings, “"), _c('em', [_vm._v("Mystères limpides:")]), _vm._v(" Time and Transformation in Debussy’s "), _c('em', [_vm._v("Des pas sur la neige,")]), _vm._v("” "), _c('em', [_vm._v("19th-Century Music")]), _vm._v(" 32/2 (2008): 178–208.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-180e189c","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/chapter02.vue
// module id = 84
// module chunks = 0